import { useEffect, useRef } from "react";
import { Form, Modal } from "antd";
import dayjs from "dayjs";
import CommonDatePicker from "@/common/elements/DatePicker";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import TimePicker from "@/common/elements/TimePicker";
import { StyledGridCol, StyledGridRow, StyledSectionHeading } from "@/common/styles";
import { getOptionsFromRefList } from "@/util/common_function";
import useActiveWarrant from "@/hooks/useActiveWarrant";
import { useConfigDefault } from '@/hooks/useConfigDefault';

const WarrantCaseInformation = ({ form: warrantForm, onStatusChange }) => {
  const [form] = Form.useForm();
  const { activeWarrant, bookingsFieldsEnabled, patch } = useActiveWarrant();

  const oriValue = useConfigDefault('Warrants', 'ORI');
  const stripQuotes = (val) => val?.replace(/^"|"$/g, '').trim() ?? '';

  // Debounce refs for text fields
  const debounceOri        = useRef(null);
  const debounceCrn        = useRef(null);
  const debounceOtn        = useRef(null);
  const debounceAssignedTo = useRef(null);

  const toTimeFromArray = (val) => {
    if (!val) return null;
    if (Array.isArray(val) && val.length >= 2)
      return dayjs().hour(val[0]).minute(val[1]).second(0);
    const d = dayjs(val, "HH:mm");
    return d.isValid() ? d : null;
  };

  const toDayjs = (val) => {
    if (!val) return null;
    const d = dayjs(val);
    return d.isValid() ? d : null;
  };

  // Hydrate form only when the warrant itself changes, not on every patch
  useEffect(() => {
    if (!activeWarrant) return;

    const offenseTime = toTimeFromArray(activeWarrant?.offenseTime);
    const oriDefault = stripQuotes(oriValue);

    form.setFieldsValue({
      offenseDate:             toDayjs(activeWarrant?.offenseDate),
      offenseTime,
      offenceCity:             activeWarrant?.offenceCity            ?? null,
      ori:                     activeWarrant?.ori || oriDefault,  
      crnNo:                   activeWarrant?.crnNo                  ?? "",
      otn:                     activeWarrant?.otn                    ?? "",
      arrestingOfficerStaffId: activeWarrant?.arrestingOfficerStaffId ?? null,
      assignedTo:              activeWarrant?.assignedTo             ?? "",
    });

    if (!activeWarrant?.ori && oriDefault) {
      patch({ ori: oriDefault });
    }
    // Normalize array time to string in Redux on first load
    if (Array.isArray(activeWarrant?.offenseTime)) {
      patch({ offenseTime: offenseTime ? dayjs(offenseTime).format("HH:mm") : null });
    }
  }, [activeWarrant?.warrantId]);

  // Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      [debounceOri, debounceCrn, debounceOtn, debounceAssignedTo]
        .forEach((r) => { if (r.current) clearTimeout(r.current); });
    };
  }, []);

  const patchField = (key, val) => patch({ [key]: val });

  const makeDebouncedPatch = (timerRef, key) => (val) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => patch({ [key]: val }), 300);
  };

  const patchOri        = makeDebouncedPatch(debounceOri,        "ori");
  const patchCrn        = makeDebouncedPatch(debounceCrn,        "crnNo");
  const patchOtn        = makeDebouncedPatch(debounceOtn,        "otn");
  const patchAssignedTo = makeDebouncedPatch(debounceAssignedTo, "assignedTo");

  const handleArrestingOfficerChange = (v) => {
    const newVal = v?.value ?? v;
    patchField("arrestingOfficerStaffId", newVal);

    const statusOptions = getOptionsFromRefList("Status", false, false, true);
    const clearedOption = statusOptions.find((o) => o.label?.toUpperCase() === "CLEARED");

    if (activeWarrant?.statusId !== clearedOption?.value && newVal) {
      Modal.confirm({
        title: "Status Update",
        content: "You have selected an officer from Arresting officer field, but the status of the warrant is not CLEARED.",
        // onOk() {
        //   const clearanceReasonOptions = getOptionsFromRefList(
        //     "Warrant_Clearance_Reason", false, false, true
        //   );
        //   const arrestedReason = clearanceReasonOptions.find(
        //     (o) => o.label?.toUpperCase() === "ARRESTED"
        //   );

        //   const userDetails = JSON.parse(localStorage.getItem("userDetails"));
        //   const staffId = userDetails?.staffId || userDetails?.staffID || null;

        //   const now = dayjs();
        //   const clearanceDate = now.format("YYYY-MM-DD");
        //   const clearanceTime = {
        //     hour:   now.hour(),
        //     minute: now.minute(),
        //     second: now.second(),
        //     nano:   0,
        //   };

        //   if (clearedOption) {
        //     patch({ statusId: clearedOption.value });
        //     warrantForm?.setFieldsValue({ statusId: clearedOption.value });
        //     onStatusChange?.(clearedOption.label);
        //   }

        //   patch({
        //     warrantClearanceInfo: [{
        //       ...(activeWarrant?.warrantClearanceInfo?.[0] ?? {}),
        //       clearanceReasonId: arrestedReason?.value ?? null,
        //       clearanceDate,
        //       clearanceTime,
        //       clearedByStaffId: staffId ? Number(staffId) : null,
        //     }],
        //   });

        //   warrantForm?.setFieldsValue({
        //     clearanceReasonId: arrestedReason?.value ?? null,
        //     clearanceDateTime: now,
        //     clearedByStaffId:  staffId ? Number(staffId) : null,
        //   });
        // },
      });
    }
  };

  return (
    <>
      <StyledSectionHeading marginTop="20px">
        <span>Case Information</span>
      </StyledSectionHeading>

      <Form form={form} disabled={!bookingsFieldsEnabled}>
        <StyledGridRow gutter={16}>
          <StyledGridCol>
            <FormItem label="Offense Date" name="offenseDate">
              <CommonDatePicker
                showTime={false}
                format="MM/DD/YYYY"
                onChange={(val) =>
                  patchField("offenseDate", val ? dayjs(val).toISOString() : null)
                }
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol>
            <FormItem label="Offense Time" name="offenseTime">
              <TimePicker
                onChange={(val) =>
                  patchField("offenseTime", val ? dayjs(val).format("HH:mm") : null)
                }
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol lg={4}>
            <FormItem label="Offense City" name="offenceCity">
              <Select
                options={getOptionsFromRefList("Arrest_City")}
                placeholder="Offense City"
                variant="outlined"
                onChange={(v) => patchField("offenceCity", v?.value ?? v)}
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol>
            <FormItem label="ORI" name="ori">
              <TextField 
                onChange={(e) => patchOri(e.target.value)}
                className="field--pink"
                maxLength={10}
              />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol>
            <FormItem label="CRN" name="crnNo">
              <TextField 
                onChange={(e) => patchCrn(e.target.value)}
                className="field--pink"
              />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol>
            <FormItem label="OTN" name="otn">
              <TextField onChange={(e) => patchOtn(e.target.value)} />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol lg={12}>
            <FormItem label="Arresting Officer" name="arrestingOfficerStaffId">
              <Select
                options={getOptionsFromRefList("", true, true, true)}
                placeholder="Arresting Officer"
                variant="outlined"
                onChange={handleArrestingOfficerChange}
                disabled={!bookingsFieldsEnabled}
              />
            </FormItem>
          </StyledGridCol>

          <StyledGridCol lg={12}>
            <FormItem label="Assigned To" name="assignedTo">
              <TextField onChange={(e) => patchAssignedTo(e.target.value)} />
            </FormItem>
          </StyledGridCol>
        </StyledGridRow>
      </Form>
    </>
  );
};

export default WarrantCaseInformation;
